import React from "react";
import { useField, useForm } from "react-final-form";
import { useTranslation } from "react-i18next";
import { allAndroidVersions, androidVersionOptions } from "src/data/products";
import i18n from "src/i18n/config";
import { PermissionService } from "src/services/permissionService/permissionService";
import { useDispatch, useSelector } from "src/store";
import { FileSource, Sources } from "src/types/osbuilder";
import { selectGroupById } from "src/ui/containers/dashboard/groups/store/selectors";
import { useVisibilityOptions } from "src/ui/containers/dashboard/shared/useVisibilityOptions";
import { Tooltip } from "src/ui/shared/Tooltip";
import { makeStyles } from "tss-react/mui";
import { v4 as uuidv4 } from "uuid";

import { showDetails } from "@dashboard/devices/store";
import { AndroidModule, Product } from "@dashboard/products/types";
import { selectProvisionings } from "@dashboard/provisioning/store/selectors";
import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
} from "@dashboard/shared/components/CollapsibleSection";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { Box, Chip, InputAdornment, Theme, useTheme } from "@mui/material";
import UserSettingsService from "@services/userSettingsService";
import { CustomBadge } from "@shared/CustomBadge";
import { ControlledSelectField, SelectField } from "@shared/form/SelectField/SelectField";
import { MultipleSelectFieldProps, SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";
import { TextField, UncontrolledTextField } from "@shared/form/TextField";

import { BuildVariantLabel } from "../../Builds/shared";
import { getCodenamePrefix, parseNightlyPeriod, parseNullableText } from "../ProductBuilder.utils";

import { ProjectSection } from "./ProjectSection/ProjectSection";
import { HardwareInput } from "./HardwareInput";
import { VariableSection } from "./VariableSection";

export const createSource = (file: API.File, sources: Sources): [string, Sources] => {
    let sourceId = uuidv4();
    const sourceIndex = Object.values(sources).findIndex((item) => item.fileId === file.id);
    if (sourceIndex !== -1) {
        sourceId = Object.keys(sources)[sourceIndex];
    }

    const newSource: FileSource = {
        fileId: file.id,
        filename: file.filename,
        workspaceId: file.workspaceId,
    };
    if (file.contentType) {
        newSource.type = "storage";
    }
    if (file.metadata?.version) {
        newSource.version = file.metadata.version;
    }

    return [sourceId, { ...sources, [sourceId]: newSource }];
};

const useStyles = makeStyles()((theme: Theme) => ({
    container: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(5),
        flex: 1,
    },
    collapsibleSection: { display: "flex", gap: theme.spacing(5), flexDirection: "column", paddingBottom: theme.spacing(5) },
    hintSection: { display: "flex", gap: theme.spacing(5), alignItems: "center" },
    visibilitySelect: { backgroundColor: theme.palette.emteria.text.inverse },
    buttonSection: { display: "flex", gap: theme.spacing(3) },
    sectionBox: { display: "flex", flexDirection: "column" },
    chips: {
        display: "flex",
        flexWrap: "wrap",
    },
    chip: {
        margin: theme.spacing(0.5),
    },
    disabledChip: {
        backgroundColor: theme.palette.emteria.background.paperDeep,
        color: theme.palette.emteria.text.secondary,
        borderColor: theme.palette.emteria.border.primary,
    },
}));

const generateVersionOptions = (value: string | undefined) => {
    const versionNames = Object.keys(androidVersionOptions);
    const versions = value && versionNames.includes(value) ? androidVersionOptions[value] : allAndroidVersions;
    return versions.map((version) => ({
        label: version,
        value: version,
    }));
};

export const buildVariantOptions = [
    { label: i18n.t("common:section.buildVariant.user.title"), value: "user" },
    { label: i18n.t("common:section.buildVariant.userdebug.title"), value: "userdebug" },
    { label: i18n.t("common:section.buildVariant.eng.title"), value: "eng" },
];

const fullOnly = "fullOnly";
const fullAndIncremental = "fullAndIncremental";

const otaPackageOptions = [
    { label: i18n.t("pages:productPage.input.otaPackages.options.fullOnly"), value: fullOnly },
    { label: i18n.t("pages:productPage.input.otaPackages.options.fullAndIncremental"), value: fullAndIncremental },
];

const otaPackagesFieldConfig = {
    format: (value: boolean | undefined) => (value ? fullOnly : fullAndIncremental),
    parse: (value: string | number | undefined) => value === fullOnly,
};

const actionOptions = [
    { label: i18n.t("pages:productPage.input.actions.options.build"), value: "Build" },
    { label: i18n.t("pages:productPage.input.actions.options.import"), value: "Import" },
];

type Props = {
    product: Product | undefined;
    isCreationDisabled: boolean;
    creationLockTooltip: string;
};
export const Recipes = (props: Props) => {
    const { classes } = useStyles();
    const theme = useTheme();
    const dispatch = useDispatch();
    const form = useForm<Product>();
    const { t } = useTranslation(["common", "pages"]);

    const formErrors = form.getState().errors || {};
    const { input: provisioningIdInput } = useField<number | null>("customBuildConfig.provisioningId");
    const { input: rootDeviceInput } = useField<string | undefined>("customBuildConfig.rootDevice");
    const { input: groupIdField } = useField<number | undefined>("groupId");
    const { input: codenameField } = useField("codename");
    const { input: androidVersionField } = useField("androidVersion", {
        subscription: { value: true, initial: true },
    });
    const { input: compatibilityField } = useField<string | undefined>("customBuildConfig.compatibility");
    const { input: actionsField } = useField<string[]>("actionFeatures");
    const { input: modulesInput } = useField<AndroidModule[]>("customBuildConfig.modules");

    const { data: workspace } = useCurrentWorkspace();
    const visibilityId = (groupIdField.value || workspace?.id) as number;
    const visibilityGroup = useSelector((state) => selectGroupById(state, visibilityId));
    const visibilityOptions = useVisibilityOptions();

    const productFlags = props.product?.visibility ?? [];
    const isPublicFirmware = productFlags.includes("PublicFirmware");
    const publicProperties = [
        productFlags.includes("PublicInheritance") ? t("pages:adminProductsPage.visibility.publicInheritance") : null,
        productFlags.includes("PublicDeviceRegistration")
            ? t("pages:adminProductsPage.visibility.publicDeviceRegistration")
            : null,
    ].filter((property) => property !== null);
    const ownerName = visibilityId === workspace?.id ? workspace?.name : visibilityGroup?.name;
    const productVisibilityText = [
        isPublicFirmware ? t("pages:adminProductsPage.visibility.publicFirmware") : ownerName,
        ...publicProperties,
    ]
        .filter(Boolean)
        .join(" · ");

    let productVisibilityTooltip = t("pages:productPage.tooltip.productsCannotBeMoved");
    if (isPublicFirmware) {
        productVisibilityTooltip = t("pages:productPage.tooltip.publicFirmware");
    } else if (publicProperties.length) {
        productVisibilityTooltip = t("pages:productPage.tooltip.productsCannotBeMovedPublicProperties");
    }

    const isEditing = !!props.product;
    const { isCreationDisabled, creationLockTooltip } = props;

    const { ProductDetails, OsConfiguration, BuildConfiguration } = UserSettingsService.getProductPageCollapsedSections();
    const productDetailsInitiallyOpen = isEditing ? (ProductDetails ?? true) : true;
    const osConfigurationInitiallyOpen = isEditing ? OsConfiguration : true;
    const buildConfigurationInitiallyOpen = isEditing ? BuildConfiguration : true;

    const versionOptions = generateVersionOptions(rootDeviceInput.value);

    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");

    const provisionings = useSelector(selectProvisionings);

    const provisioningFiles = provisionings.map((item) => ({
        label: item.name,
        value: item.id,
    }));

    const onProvisioningIdChange: SingleSelectFieldProps["onChange"] = (_event, selectedOption) => {
        if (typeof selectedOption?.value === "string") {
            return;
        }

        provisioningIdInput.onChange(selectedOption?.value ?? null);
    };
    const isCompatibilityEmpty = () => {
        const value = compatibilityField.value;
        return !value || value.trim() === "";
    };

    const shouldUpdateCompatibility = () => {
        if (!isEditing) {
            return true;
        }
        return isCompatibilityEmpty();
    };

    const onVersionChange: SingleSelectFieldProps["onChange"] = (_event, selectedOption) => {
        if (!selectedOption || typeof selectedOption.value !== "string") {
            return;
        }

        if (shouldUpdateCompatibility()) {
            compatibilityField.onChange(`>=${selectedOption.value}`);
        }

        androidVersionField.onChange(selectedOption.value);
    };
    const onActionsChange: MultipleSelectFieldProps["onChange"] = (_event, selectedOption) => {
        actionsField.onChange(selectedOption.map((option) => option.value));
    };
    const onVisibilityChange: SingleSelectFieldProps["onChange"] = (_event, selectedOption) => {
        if (typeof selectedOption?.value === "string") {
            return;
        }

        const value = selectedOption?.value;

        if (value === workspace?.id) {
            groupIdField.onChange(null);
        }
        if (value !== workspace?.id) {
            groupIdField.onChange(value);
        }
    };
    const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        if (codenameField.value === "") {
            codenameField.onChange(e.target.value.toLowerCase().replace(/ /g, "_"));
        }
    };
    const onProductDetailsChange = (open: boolean) => {
        if (!isEditing) {
            return;
        }
        UserSettingsService.setWorkspaceValue("productPage.collapsedSections.ProductDetails", open);
    };
    const onOsConfigurationChange = (open: boolean) => {
        if (!isEditing) {
            return;
        }
        UserSettingsService.setWorkspaceValue("productPage.collapsedSections.OsConfiguration", open);
    };
    const onBuildConfigurationChange = (open: boolean) => {
        if (!isEditing) {
            return;
        }
        UserSettingsService.setWorkspaceValue("productPage.collapsedSections.BuildConfiguration", open);
    };
    const onModules = () => {
        const productId = props.product?.id;
        if (!productId) {
            return;
        }
        dispatch(showDetails({ type: "module", selectedId: productId }));
    };

    const shouldShowCreationLockTooltip = isCreationDisabled && !cannotManageProducts;

    const prevAndroidVersionRef = React.useRef<string | undefined>(
        typeof androidVersionField.value === "string" ? androidVersionField.value : undefined,
    );

    React.useEffect(() => {
        const currentVersion = androidVersionField.value;
        const prevVersion = prevAndroidVersionRef.current;

        if (currentVersion && currentVersion !== prevVersion && shouldUpdateCompatibility()) {
            compatibilityField.onChange(`>=${currentVersion}`);
        }

        prevAndroidVersionRef.current = currentVersion;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reacts to the Android version; the rest is only read at that moment
    }, [androidVersionField.value, isEditing, compatibilityField]);

    return (
        <Box className={classes.container}>
            <Box className={classes.sectionBox}>
                <CollapsibleProvider
                    key={`product-details-${isEditing}`}
                    initialValue={productDetailsInitiallyOpen}
                    onChange={onProductDetailsChange}
                >
                    <CollapsibleSectionSummary>
                        <CollapsibleSectionTitle>{t("pages:productPage.productDetails")}</CollapsibleSectionTitle>
                    </CollapsibleSectionSummary>
                    <CollapsibleSectionDetails>
                        <Box className={classes.collapsibleSection} style={{ marginTop: theme.spacing(2) }}>
                            <Box className={classes.hintSection}>
                                <Tooltip title={creationLockTooltip} hide={!shouldShowCreationLockTooltip} disableChildrenWrapper>
                                    <TextField
                                        name="customBuildConfig.productName"
                                        label={t("pages:productPage.input.productName.caption")}
                                        placeholder={t("pages:productPage.input.productName.placeholder")}
                                        onBlur={onBlur}
                                        fullWidth
                                        disabled={cannotManageProducts || isCreationDisabled}
                                    />
                                </Tooltip>

                                <Tooltip title={t("pages:productPage.tooltip.productName")}>
                                    <i className="fa-solid fa-circle-info" />
                                </Tooltip>
                            </Box>

                            <Box className={classes.hintSection}>
                                <Tooltip title={creationLockTooltip} hide={!shouldShowCreationLockTooltip} disableChildrenWrapper>
                                    <TextField
                                        name="codename"
                                        label={t("pages:productPage.input.codename.caption")}
                                        placeholder={t("pages:productPage.input.codename.placeholder")}
                                        fullWidth
                                        disabled={isEditing || cannotManageProducts || isCreationDisabled}
                                        style={{ flex: 1 }}
                                        slotProps={{
                                            input: {
                                                startAdornment: !isEditing ? (
                                                    <InputAdornment position="start">
                                                        <span>{getCodenamePrefix(workspace?.guid)}</span>
                                                    </InputAdornment>
                                                ) : undefined,
                                            },
                                        }}
                                    />
                                </Tooltip>

                                <Tooltip title={t("pages:productPage.tooltip.codenamePrefix")}>
                                    <i className="fa-solid fa-circle-info" />
                                </Tooltip>
                            </Box>

                            <Box className={classes.hintSection}>
                                <Tooltip title={creationLockTooltip} hide={!shouldShowCreationLockTooltip} disableChildrenWrapper>
                                    <TextField
                                        name="description"
                                        config={{ parse: parseNullableText }}
                                        label={t("pages:productPage.input.description.label")}
                                        placeholder={t("pages:productPage.input.description.placeholder")}
                                        multiline
                                        minRows={3}
                                        fullWidth
                                        disabled={cannotManageProducts || isCreationDisabled}
                                    />
                                </Tooltip>

                                <Tooltip title={t("pages:productPage.tooltip.description")}>
                                    <i className="fa-solid fa-circle-info" />
                                </Tooltip>
                            </Box>

                            <Box className={classes.hintSection}>
                                <Tooltip title={creationLockTooltip} hide={!shouldShowCreationLockTooltip} disableChildrenWrapper>
                                    <TextField
                                        name="documentationUrl"
                                        config={{ parse: parseNullableText }}
                                        label={t("pages:productPage.input.documentationUrl.caption")}
                                        placeholder={t("pages:productPage.input.documentationUrl.placeholder")}
                                        fullWidth
                                        disabled={cannotManageProducts || isCreationDisabled}
                                    />
                                </Tooltip>

                                <Tooltip title={t("pages:productPage.tooltip.documentationUrl")}>
                                    <i className="fa-solid fa-circle-info" />
                                </Tooltip>
                            </Box>
                            <Box className={classes.hintSection}>
                                {isEditing || cannotManageProducts ? (
                                    <UncontrolledTextField
                                        name="groupId"
                                        label={t("pages:productPage.input.productVisibility.caption")}
                                        errorMessage={formErrors["groupId"]}
                                        value={productVisibilityText}
                                        disabled
                                        fullWidth
                                    />
                                ) : (
                                    <Tooltip
                                        title={creationLockTooltip}
                                        hide={!shouldShowCreationLockTooltip}
                                        disableChildrenWrapper
                                    >
                                        <SelectField
                                            label={t("pages:productPage.input.productVisibility.caption")}
                                            value={visibilityOptions.find((option) => option.value === visibilityId)}
                                            options={visibilityOptions}
                                            errorMessage={formErrors["groupId"]}
                                            onChange={onVisibilityChange}
                                            className={classes.visibilitySelect}
                                            fullWidth
                                            disableSorting
                                            disabled={isCreationDisabled}
                                        />
                                    </Tooltip>
                                )}

                                <Tooltip title={productVisibilityTooltip}>
                                    <i className="fa-solid fa-circle-info" />
                                </Tooltip>
                            </Box>
                        </Box>
                    </CollapsibleSectionDetails>
                </CollapsibleProvider>

                <CollapsibleProvider
                    key={`os-configuration-${isEditing}`}
                    initialValue={osConfigurationInitiallyOpen}
                    onChange={onOsConfigurationChange}
                >
                    <CollapsibleSectionSummary>
                        <CollapsibleSectionTitle>{t("pages:productPage.section.osConfiguration")}</CollapsibleSectionTitle>
                    </CollapsibleSectionSummary>
                    <CollapsibleSectionDetails>
                        <Tooltip
                            title={t("pages:productPage.tooltip.noPermissionToChangeOsConfiguration")}
                            hide={!cannotManageProducts}
                            placement="right"
                            childrenBoxStyles={{ display: "block", width: "100%" }}
                        >
                            <Box className={classes.collapsibleSection} style={{ marginTop: theme.spacing(2) }}>
                                <HardwareInput />

                                <SelectField
                                    label={t("pages:productPage.input.androidVersion.caption")}
                                    value={versionOptions.find((option) => option.value === androidVersionField.value)}
                                    options={versionOptions}
                                    errorMessage={formErrors["androidVersion"]}
                                    onChange={onVersionChange}
                                    disabled={cannotManageProducts}
                                    fullWidth
                                />

                                <SelectField
                                    label={t("pages:productPage.input.provisioningSettings.caption")}
                                    value={provisioningFiles.find((option) => option.value === provisioningIdInput.value)}
                                    options={provisioningFiles}
                                    errorMessage={formErrors["customBuildConfig.provisioningId"]}
                                    onChange={onProvisioningIdChange}
                                    disabled={!provisioningFiles.length || cannotManageProducts}
                                    disableClearable={false}
                                    fullWidth
                                />
                            </Box>
                        </Tooltip>

                        <Box className={classes.buttonSection}>
                            <ProjectSection isCreationDisabled={isCreationDisabled} creationLockTooltip={creationLockTooltip} />
                            <Tooltip title={t("pages:productPage.tooltip.availableAfterProductCreation")} hide={isEditing}>
                                <CustomBadge badgeContent={(modulesInput.value || []).length}>
                                    <Chip
                                        label={t("pages:productPage.modules")}
                                        onClick={onModules}
                                        icon={<i className="fa-solid fa-plus" />}
                                        disabled={!isEditing}
                                    />
                                </CustomBadge>
                            </Tooltip>
                        </Box>
                    </CollapsibleSectionDetails>
                </CollapsibleProvider>

                <CollapsibleProvider
                    key={`build-configuration-${isEditing}`}
                    initialValue={buildConfigurationInitiallyOpen}
                    onChange={onBuildConfigurationChange}
                >
                    <CollapsibleSectionSummary>
                        <CollapsibleSectionTitle>{t("pages:productPage.section.buildConfiguration")}</CollapsibleSectionTitle>
                    </CollapsibleSectionSummary>
                    <CollapsibleSectionDetails>
                        <Tooltip
                            title={t("pages:productPage.tooltip.noPermissionToChangeBuildConfiguration")}
                            hide={!cannotManageProducts}
                            placement="right"
                            childrenBoxStyles={{ display: "block", width: "100%" }}
                        >
                            <Box className={classes.collapsibleSection} style={{ marginTop: theme.spacing(2) }}>
                                <Box className={classes.hintSection}>
                                    <Tooltip
                                        title={creationLockTooltip}
                                        hide={!shouldShowCreationLockTooltip}
                                        disableChildrenWrapper
                                    >
                                        <SelectField
                                            label={t("pages:productPage.input.actions.caption")}
                                            value={actionOptions.filter((option) => actionsField.value.includes(option.value))}
                                            options={actionOptions}
                                            onChange={onActionsChange}
                                            disabled={cannotManageProducts || isCreationDisabled}
                                            multiple
                                            fullWidth
                                        />
                                    </Tooltip>
                                    <Tooltip title={t("pages:productPage.tooltip.actions")}>
                                        <i className="fa-solid fa-circle-info" />
                                    </Tooltip>
                                </Box>
                                <Box className={classes.hintSection}>
                                    <Tooltip
                                        title={creationLockTooltip}
                                        hide={!shouldShowCreationLockTooltip}
                                        disableChildrenWrapper
                                    >
                                        <TextField
                                            name="customBuildConfig.compatibility"
                                            label={i18n.t("pages:productPage.input.compatibility.caption")}
                                            placeholder={i18n.t("pages:productPage.input.compatibility.placeholder")}
                                            fullWidth
                                            disabled={cannotManageProducts || isCreationDisabled}
                                        />
                                    </Tooltip>
                                    <Tooltip title={i18n.t("pages:productPage.tooltip.compatibility")}>
                                        <i className="fa-solid fa-circle-info" />
                                    </Tooltip>
                                </Box>
                                <Box className={classes.hintSection}>
                                    <ControlledSelectField
                                        fullWidth
                                        label={t("pages:productPage.input.buildVariant.caption")}
                                        name="customBuildConfig.buildVariant"
                                        options={buildVariantOptions}
                                        disabled={cannotManageProducts}
                                    />
                                    <BuildVariantLabel />
                                </Box>
                                <Box className={classes.hintSection}>
                                    <ControlledSelectField
                                        fullWidth
                                        name="customBuildConfig.ota.disableIncremental"
                                        label={t("pages:productPage.input.otaPackages.caption")}
                                        options={otaPackageOptions}
                                        config={otaPackagesFieldConfig}
                                        disableSorting
                                        disabled={cannotManageProducts}
                                    />
                                    <Tooltip title={t("pages:productPage.tooltip.otaPackages")}>
                                        <i className="fa-solid fa-circle-info" />
                                    </Tooltip>
                                </Box>
                                <Box className={classes.hintSection}>
                                    <Tooltip
                                        title={creationLockTooltip}
                                        hide={!shouldShowCreationLockTooltip}
                                        disableChildrenWrapper
                                    >
                                        <TextField
                                            label={t("pages:productPage.input.nightlyPeriod.caption")}
                                            name="nightlyPeriod"
                                            config={{ parse: parseNightlyPeriod }}
                                            slotProps={{ htmlInput: { inputMode: "numeric" } }}
                                            placeholder={t("pages:productPage.input.nightlyPeriod.placeholder")}
                                            fullWidth
                                            disabled={cannotManageProducts || isCreationDisabled}
                                        />
                                    </Tooltip>

                                    <Tooltip title={t("pages:productPage.tooltip.nightlyPeriod")}>
                                        <i className="fa-solid fa-circle-info" />
                                    </Tooltip>
                                </Box>
                            </Box>
                        </Tooltip>

                        <Box className={classes.buttonSection}>
                            {props.product ? (
                                <VariableSection productId={props.product.id} />
                            ) : (
                                <Tooltip title={t("pages:productPage.tooltip.availableAfterProductCreation")}>
                                    <CustomBadge badgeContent={0}>
                                        <Chip
                                            label={t("pages:productPage.variables")}
                                            icon={<i className="fa-solid fa-plus" />}
                                            disabled
                                        />
                                    </CustomBadge>
                                </Tooltip>
                            )}
                        </Box>
                    </CollapsibleSectionDetails>
                </CollapsibleProvider>
            </Box>
        </Box>
    );
};
